import styled from 'styled-components';
import type { PtoVolumePosition } from '@shared/api';
import { manualAnchorOf, targetOf } from '../rdSheet/protocol';
import { rowLabel, sourcePlace } from './rowRef';
import {
  checkLabel,
  checkTitle,
  confirmedChecks,
  fullPlace,
  hiddenCheckTexts,
  qtyText,
  sheetCheckMarks,
  sumBadge,
  sumHint,
  vorCandidate,
  type CheckTone,
} from './sheetChecks';
import { Badge } from './styles';
import type { VolumeCellContext } from './columns';

/** Ссылка-кнопка в ячейке: открывает лист в окне исходного листа РД. */
export const SheetLink = styled.button`
  display: inline;
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  color: inherit;
  text-align: left;
  text-decoration: underline dotted;
  text-underline-offset: 3px;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
  &:focus-visible {
    outline: none;
    border-radius: ${({ theme }) => theme.radius.xs};
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

const Checks = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
`;

const Check = styled(SheetLink)`
  white-space: nowrap;
  font-size: 12px;
  text-decoration: none;
  color: ${({ theme }) => theme.colors.status.success};

  &:hover {
    text-decoration: underline;
  }
`;

const More = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: help;
`;

/** Отметка графы сверки выпуска 2: «✓» — зелёным, «⚠» и пометки строк —
 *  цветом предупреждения, «и ещё N» — приглушённо. Ссылка — отметка учтённой
 *  строки (открывает её лист). `font-feature-settings: normal` — «⚠» запасным
 *  шрифтом Windows без стилистических наборов (как значок в «Разделе»). */
function toneColor(tone: CheckTone): 'success' | 'warning' | null {
  if (tone === 'ok') return 'success';
  return tone === 'more' ? null : 'warning';
}

const MarkText = styled.span<{ $tone: CheckTone }>`
  font-size: 12px;
  font-feature-settings: normal;
  color: ${({ theme, $tone }) => {
    const c = toneColor($tone);
    return c ? theme.colors.status[c] : theme.colors.text.secondary;
  }};
`;

const MarkLink = styled(SheetLink)<{ $tone: CheckTone }>`
  font-size: 12px;
  font-feature-settings: normal;
  text-decoration: none;
  color: ${({ theme, $tone }) => {
    const c = toneColor($tone);
    return c ? theme.colors.status[c] : theme.colors.text.secondary;
  }};

  &:hover {
    text-decoration: underline;
  }
`;

/** Подсказка к отметке выпуска 2: где учтённая строка, её наименование и число. */
function checkTitleOf(row: PtoVolumePosition): string {
  const name = row.name.replace(/\s+/g, ' ').trim();
  const label = rowLabel(row);
  const at = fullPlace(row);
  return `${[label, at].filter(Boolean).join(', ')}: «${name}» — ${qtyText(row.quantity, row.unit)}. Открыть лист`;
}

/** Подпись строки для подсказок: «СП01 №2 «Реостат…» (СП01 л. 1, стр. PDF 7)». */
export function rowCaption(p: PtoVolumePosition): string {
  const label = rowLabel(p);
  const name = p.name.replace(/\s+/g, ' ').trim();
  const short = name.length > 60 ? `${name.slice(0, 60).trimEnd()}…` : name;
  const at = fullPlace(p);
  return [label, `«${short}»`, at ? `(${at})` : null].filter(Boolean).join(' ');
}

/** Цель окна листа для позиции экрана (подписи — как в графах «№» и «Источник»).
 *  `positions` — строки ревизии: у ручной строки №8 своей рамки нет, окно
 *  подсвечивает строку, после которой она внесена. */
export function sheetTargetFor(
  p: PtoVolumePosition,
  docCode: string | null,
  positions: Iterable<PtoVolumePosition> = [],
) {
  const label = rowLabel(p);
  const name = p.name.replace(/\s+/g, ' ').trim();
  return targetOf(
    p,
    {
      title: label ? `${label} — ${name}` : name,
      place: sourcePlace(p),
      qty: qtyText(p.quantity, p.unit),
      docCode,
    },
    manualAnchorOf(p, positions),
  );
}

interface Props {
  position: PtoVolumePosition;
  ctx: VolumeCellContext;
}

/** Признак отбора в «Статусе»: «в сумме», «повтор», «вне суммы» — из того же
 *  отбора, что и ведомость материалов; причина и строка, которая учла число, —
 *  в подсказке. Признаков ещё нет — ничего. */
export function SumMark({ position: p, ctx }: Props) {
  const mark = ctx.marks.get(p.id);
  const badge = sumBadge(mark);
  if (!badge) return null;
  const countedId = mark?.counted_as ?? mark?.possible_dup_of;
  const counted = countedId ? ctx.byId.get(countedId) : undefined;
  const hint = sumHint(mark, counted ? rowCaption(counted) : null);
  return (
    <>
      {/* Спокойный тон: «в сумме» — обычное состояние строки, внимания требуют
          «на проверку» и решения инженера рядом. */}
      <Badge $tone="muted" title={hint}>
        {badge}
      </Badge>{' '}
    </>
  );
}

/** «Сверка с листами РД».
 *  - Правила выпуска 2: текст графы бэкенда, как в Excel, — «✓ стр. 6 поз. 2
 *    (4 шт)», «⚠ стр. 6 поз. 5: 1 шт против 2 шт», «✓ ВР01 л. 37», пометки
 *    («нет в СП01 — проверьте»); отметка учтённой строки — ссылка на её лист.
 *    У строки «Есть в ВОР, нет в СП» — кандидат пары из СП (ссылка на его лист)
 *    или «нет в СП».
 *  - Без них (выпуск 1б, флаг выключен): «✓ л. 6 поз. 2 (4 шт)» — строки листов
 *    РД, учтённые в этой строке, при равных количестве и единице.
 *  Клик открывает лист строки в окне исходного листа. */
export function SheetCheckCell({ position: p, ctx }: Props) {
  const mark = ctx.marks.get(p.id);
  const vor = vorCandidate(mark);
  if (vor) {
    const cand = vor.target ? ctx.byId.get(vor.target) : undefined;
    return (
      <Checks>
        <MarkText $tone="warn">есть в ВОР, нет в СП — проверьте</MarkText>
        {cand ? (
          <MarkLink
            type="button"
            $tone="more"
            title={checkTitleOf(cand)}
            onClick={() => ctx.onOpenSheet(cand)}
          >
            {vor.text}
          </MarkLink>
        ) : (
          <MarkText $tone="more">{vor.text}</MarkText>
        )}
      </Checks>
    );
  }
  const marks = sheetCheckMarks(mark);
  if (marks.length > 0) {
    const hidden = hiddenCheckTexts(mark, marks);
    return (
      <Checks>
        {marks.map((m, i) => {
          const row = m.target ? ctx.byId.get(m.target) : undefined;
          if (row) {
            return (
              <MarkLink
                key={`${i}-${m.text}`}
                type="button"
                $tone={m.tone}
                title={checkTitleOf(row)}
                onClick={() => ctx.onOpenSheet(row)}
              >
                {m.text}
              </MarkLink>
            );
          }
          return (
            <MarkText
              key={`${i}-${m.text}`}
              $tone={m.tone}
              title={m.tone === 'more' && hidden.length > 0 ? hidden.join('\n') : undefined}
            >
              {m.text}
            </MarkText>
          );
        })}
      </Checks>
    );
  }
  const rows = confirmedChecks(mark)
    .map((c) => ctx.byId.get(c.id))
    .filter((r): r is PtoVolumePosition => !!r);
  if (rows.length === 0) return null;
  const shown = rows.slice(0, 3);
  const rest = rows.slice(3);
  return (
    <Checks>
      {shown.map((r) => (
        <Check key={r.id} type="button" title={checkTitle(r)} onClick={() => ctx.onOpenSheet(r)}>
          {checkLabel(r)}
        </Check>
      ))}
      {rest.length > 0 && (
        <More title={rest.map((r) => checkLabel(r)).join('\n')}>и ещё {rest.length}</More>
      )}
    </Checks>
  );
}
